import React from "react";
import PropTypes from "prop-types";
import { Box, TextField } from "@mui/material";
import {
  AddressFields,
  CitizenshipField,
  CountryField,
} from "../../../../../components/guardian/ManualEntryFields";
import { HISTORIC_GUARDIAN_BOND } from "../../historicGuardianConfig";
import { gVal } from "../../historicGuardianUtils";
import HistoricEmailField from "./HistoricEmailField";

export default function HistoricManualEntrySection({
  bondKind,
  index,
  guardian,
  guardians,
  cfg,
  keys,
  ms,
  data,
  updateData,
  GoogleAutoComplete,
  LabeledCreatableSelect,
  allCities,
  allStates,
  loadingCitiesStates,
  handleGoogleCallback,
  handleZipCodeChange,
  showHighBondSsn,
  showManualOccupationField,
  showRelationshipField,
  formatSSN,
  emailExists,
  emailChecking,
  emailDialogOpen,
  getDebouncedCheckEmail,
  handleDialogClose,
  handleDialogContinue,
  handleDialogCancel,
  onGuardianEmailCommit,
}) {
  const mergeGuardians = (next) =>
    updateData({ ...data, guardians: next });

  return (
    <Box className="grid grid-cols-1 gap-4">
      <TextField
        sx={ms.name}
        label="Name"
        value={gVal(guardian, keys, "name")}
        onChange={(e) => {
          const inputValue = e.target.value;
          const updatedGuardians = [...guardians];
          const isG = bondKind === HISTORIC_GUARDIAN_BOND.guardianship;
          updatedGuardians[index] = {
            ...updatedGuardians[index],
            [keys.name]: inputValue,
            [keys.citizenship]: "USA",
            ...(isG ? { country: "USA" } : { [keys.country]: "USA" }),
          };
          mergeGuardians(updatedGuardians);
        }}
        fullWidth
        required
        slotProps={{ htmlInput: { maxLength: 255 }}}
      />

      <HistoricEmailField
        cfg={cfg}
        keys={keys}
        index={index}
        guardian={guardian}
        guardians={guardians}
        label={cfg.emailFieldLabel || "Email"}
        emailExists={emailExists}
        emailChecking={emailChecking}
        emailDialogOpen={emailDialogOpen}
        getDebouncedCheckEmail={getDebouncedCheckEmail}
        onDialogClose={handleDialogClose}
        onDialogContinue={handleDialogContinue}
        onDialogCancel={handleDialogCancel}
        onGuardianEmailCommit={onGuardianEmailCommit}
      />

      <CitizenshipField
        citizenshipSelectSx={ms.citizenshipSelect}
        specifyCitizenshipSx={ms.specifyCitizenship}
        value={gVal(guardian, keys, "citizenship")}
        customValue={gVal(guardian, keys, "customCitizenship")}
        onChange={(val) => {
          const updatedGuardians = [...guardians];
          if (val === "Other") {
            updatedGuardians[index] = {
              ...updatedGuardians[index],
              [keys.citizenship]: "Other",
            };
          } else {
            updatedGuardians[index] = {
              ...updatedGuardians[index],
              [keys.citizenship]: val,
            };
          }
          mergeGuardians(updatedGuardians);
        }}
        onCustomChange={(val) => {
          const updatedGuardians = [...guardians];
          updatedGuardians[index] = {
            ...updatedGuardians[index],
            [keys.customCitizenship]: val,
          };
          mergeGuardians(updatedGuardians);
        }}
      />

      <CountryField
        countrySelectSx={ms.countrySelect}
        specifyCountrySx={ms.specifyCountry}
        value={gVal(guardian, keys, "country")}
        customValue={gVal(guardian, keys, "customCountry")}
        onChange={(val) => {
          const updatedGuardians = [...guardians];
          if (val === "Other") {
            updatedGuardians[index] = {
              ...updatedGuardians[index],
              [keys.country]: "Other",
            };
          } else {
            updatedGuardians[index] = {
              ...updatedGuardians[index],
              [keys.country]: val,
            };
          }
          mergeGuardians(updatedGuardians);
        }}
        onCustomChange={(val) => {
          const updatedGuardians = [...guardians];
          updatedGuardians[index] = {
            ...updatedGuardians[index],
            [keys.customCountry]: val,
          };
          mergeGuardians(updatedGuardians);
        }}
      />

      {showRelationshipField(guardian) && (
        <TextField
          label={cfg.relationshipLabel}
          value={gVal(guardian, keys, "relationship")}
          onChange={(e) => {
            const updatedGuardians = [...guardians];
            updatedGuardians[index] = {
              ...updatedGuardians[index],
              [keys.relationship]: e.target.value,
            };
            mergeGuardians(updatedGuardians);
          }}
          fullWidth
          required={cfg.relationshipRequired}
        />
      )}

      <AddressFields
        address={gVal(guardian, keys, "address")}
        zipCode={gVal(guardian, keys, "zip")}
        city={gVal(guardian, keys, "city")}
        state={gVal(guardian, keys, "state")}
        googlePlaceholder={cfg.googlePlaceholderManual || "Search Address"}
        onAddressChange={(val) => {
          const ug = [...guardians];
          ug[index] = { ...ug[index], [keys.address]: val };
          mergeGuardians(ug);
        }}
        onZipChange={(e) => handleZipCodeChange(e, index)}
        onCityChange={(val) => {
          const ug = [...guardians];
          ug[index] = { ...ug[index], [keys.city]: val };
          mergeGuardians(ug);
        }}
        onStateChange={(val) => {
          const ug = [...guardians];
          ug[index] = { ...ug[index], [keys.state]: val };
          mergeGuardians(ug);
        }}
        onGoogleCallback={(address, city, state, zipCode) =>
          handleGoogleCallback(index, address, city, state, zipCode)
        }
        loading={loadingCitiesStates}
        allCities={allCities}
        allStates={allStates}
        GoogleAutoComplete={GoogleAutoComplete}
        LabeledCreatableSelect={LabeledCreatableSelect}
      />

      {showHighBondSsn() && (
        <TextField
          label="Social Security Number (SSN)"
          type="password"
          value={gVal(guardian, keys, "ssn")}
          onChange={(e) => {
            const updatedGuardians = [...guardians];
            updatedGuardians[index] = {
              ...updatedGuardians[index],
              [keys.ssn]: e.target.value,
            };
            mergeGuardians(updatedGuardians);
          }}
          fullWidth
          required
          helperText={formatSSN(gVal(guardian, keys, "ssn"))}
        />
      )}

      {showManualOccupationField(guardian) && (
        <TextField
          label="Occupation"
          value={gVal(guardian, keys, "occupation")}
          onChange={(e) => {
            const updatedGuardians = [...guardians];
            updatedGuardians[index] = {
              ...updatedGuardians[index],
              [keys.occupation]: e.target.value,
            };
            mergeGuardians(updatedGuardians);
          }}
          fullWidth
          required
        />
      )}
    </Box>
  );
}

HistoricManualEntrySection.propTypes = {
  bondKind: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  guardian: PropTypes.object.isRequired,
  guardians: PropTypes.array.isRequired,
  cfg: PropTypes.object.isRequired,
  keys: PropTypes.object.isRequired,
  ms: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  updateData: PropTypes.func.isRequired,
  GoogleAutoComplete: PropTypes.elementType.isRequired,
  LabeledCreatableSelect: PropTypes.elementType.isRequired,
  allCities: PropTypes.array.isRequired,
  allStates: PropTypes.array.isRequired,
  loadingCitiesStates: PropTypes.bool.isRequired,
  handleGoogleCallback: PropTypes.func.isRequired,
  handleZipCodeChange: PropTypes.func.isRequired,
  showHighBondSsn: PropTypes.func.isRequired,
  showManualOccupationField: PropTypes.func.isRequired,
  showRelationshipField: PropTypes.func.isRequired,
  formatSSN: PropTypes.func.isRequired,
  emailExists: PropTypes.array.isRequired,
  emailChecking: PropTypes.array.isRequired,
  emailDialogOpen: PropTypes.array.isRequired,
  getDebouncedCheckEmail: PropTypes.func.isRequired,
  handleDialogClose: PropTypes.func.isRequired,
  handleDialogContinue: PropTypes.func.isRequired,
  handleDialogCancel: PropTypes.func.isRequired,
  onGuardianEmailCommit: PropTypes.func.isRequired,
};
